/**
 * App.tsx  -  Root component.
 * Sets up routing, React Query, and silent session restore on boot.
 *
 * Role routing:
 *   platform_admin  → /admin/*  (AdminLayout)
 *   school_admin    → /school/* (SchoolAdminLayout)
 *   teacher/trainer → /dashboard, /classes, etc. (AppLayout)
 *   parent          → /parent/* (ParentLayout)
 */

import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { useSessionRestore, roleHomePath } from "@/hooks/useAuth";
import { useSocket } from "@/hooks/useSocket";
import { useEffectiveUser, useIsLoading } from "@/hooks/useEffectiveUser";

// Layouts  -  keep these eager (tiny, shared, needed immediately)
import AppLayout from "@/components/layouts/AppLayout";
import AdminLayout from "@/components/layouts/AdminLayout";
import SchoolAdminLayout from "@/components/layouts/SchoolAdminLayout";
import ParentLayout from "@/components/layouts/ParentLayout";
import StudentLayout from "@/components/layouts/StudentLayout";
import ImpersonationBanner, {
  useIsImpersonating,
} from "@/components/ImpersonationBanner";
import { useStudentAuthStore } from "@/store/studentAuthStore";

// Auth / marketing pages  -  keep eager (hit on every cold start)
import LoginPage from "@/pages/LoginPage";
import SignupPage from "@/pages/SignupPage";
import LandingPage from "@/pages/LandingPage";
import SeoLandingPage from "@/pages/SeoLandingPage";
import { SEO_PAGES } from "@/data/seoPages";
import LegalPage from "@/pages/LegalPage";
import CookieConsent from "@/components/CookieConsent";
import { PwaUpdatePrompt } from "@/components/PwaUpdatePrompt";
import { LEGAL_SLUGS } from "@/data/legalContent";

// ─── Lazy page chunks ─────────────────────────────────────────────────────────

// Teacher pages
const DashboardPage = lazy(() => import("@/pages/DashboardPage"));
const ClassesPage = lazy(() => import("@/pages/ClassesPage"));
const ClassDetailPage = lazy(() => import("@/pages/ClassDetailPage"));
const SubmissionsPage = lazy(() => import("@/pages/SubmissionsPage"));
const LessonsPage = lazy(() => import("@/pages/LessonsPage"));
const PlannerPage = lazy(() => import("@/pages/planner/PlannerPage"));
const ProgressPage = lazy(() => import("@/pages/ProgressPage"));
const MessagesPage = lazy(() => import("@/pages/MessagesPage"));
const SettingsPage = lazy(() => import("@/pages/SettingsPage"));
const SpellingPage = lazy(() => import("@/pages/SpellingPage"));
const SpellingListDetailPage = lazy(
  () => import("@/pages/SpellingListDetailPage"),
);
const CertificatesPage = lazy(() => import("@/pages/CertificatesPage"));
const GamesPage = lazy(() => import("@/pages/GamesPage"));
const ResourcesPage = lazy(() => import("@/pages/ResourcesPage"));
const BossBattlePage = lazy(() => import("@/pages/BossBattlePage"));
const BossBattleProjectorPage = lazy(
  () => import("@/pages/BossBattleProjectorPage"),
);
const JoinBattlePage = lazy(() => import("@/pages/JoinBattlePage"));
const GroupDashboardPage = lazy(() => import("@/pages/GroupDashboardPage"));

// Platform admin pages
const AdminOverviewPage = lazy(() => import("@/pages/admin/AdminOverviewPage"));
const AdminSchoolsPage = lazy(() => import("@/pages/admin/AdminSchoolsPage"));
const AdminSchoolDetail = lazy(
  () => import("@/pages/admin/AdminSchoolDetailPage"),
);
const AdminSettingsPage = lazy(() => import("@/pages/admin/AdminSettingsPage"));
const AdminSanctionsPage = lazy(() => import("@/pages/admin/AdminSanctionsPage"));
const AdminImpersonatePage = lazy(
  () => import("@/pages/admin/AdminImpersonatePage"),
);

// Parent pages
const ParentDashboardPage = lazy(
  () => import("@/pages/parent/ParentDashboardPage"),
);
const ParentMessagesPage = lazy(
  () => import("@/pages/parent/ParentMessagesPage"),
);

// School admin pages
const SchoolDashboardPage = lazy(
  () => import("@/pages/school/SchoolDashboardPage"),
);
const SchoolTeachersPage = lazy(
  () => import("@/pages/school/SchoolTeachersPage"),
);
const SchoolStudentsPage = lazy(
  () => import("@/pages/school/SchoolStudentsPage"),
);
const SchoolInviteCodesPage = lazy(
  () => import("@/pages/school/SchoolInviteCodesPage"),
);
const SchoolSettingsPage = lazy(
  () => import("@/pages/school/SchoolSettingsPage"),
);
const SchoolEnrollmentPage = lazy(
  () => import("@/pages/school/SchoolEnrollmentPage"),
);
const SchoolAnalyticsPage = lazy(
  () => import("@/pages/school/SchoolAnalyticsPage"),
);
const VerifyEmailPage = lazy(() => import("@/pages/auth/VerifyEmailPage"));
const SchoolAttendancePage = lazy(
  () => import("@/pages/school/SchoolAttendancePage"),
);
const SessionLogPage = lazy(() => import("@/pages/sessions/SessionLogPage"));
const LiveSessionHubPage = lazy(
  () => import("@/pages/sessions/LiveSessionHubPage"),
);

// Student portal pages
const StudentLoginPage = lazy(() => import("@/pages/student/StudentLoginPage"));
const StudentHomePage = lazy(() => import("@/pages/student/StudentHomePage"));
const StudentGamesPage = lazy(() => import("@/pages/student/StudentGamesPage"));
const StudentWordsPage = lazy(() => import("@/pages/student/StudentWordsPage"));
const StudentStarsPage = lazy(() => import("@/pages/student/StudentStarsPage"));
const StudentQuestPage = lazy(() => import("@/pages/student/StudentQuestPage"));
const StudentSessionPage = lazy(
  () => import("@/pages/student/StudentSessionPage"),
);
const StudentAlphabetPage = lazy(
  () => import("@/pages/student/StudentAlphabetPage"),
);
const StudentLetterPage = lazy(
  () => import("@/pages/student/StudentLetterPage"),
);
const StudentShopPage = lazy(() => import("@/pages/student/StudentShopPage"));
const StudentRoomPage = lazy(() => import("@/pages/student/StudentRoomPage"));
const StudentHomeworkPage = lazy(
  () => import("@/pages/student/StudentHomeworkPage"),
);
const StudentTaskPage = lazy(() => import("@/pages/student/StudentTaskPage"));

// ─── Shared loading spinner ───────────────────────────────────────────────────

function PageLoader() {
  return (
    <div className="flex min-h-screen items-center justify-center">
      <div className="h-8 w-8 animate-spin rounded-full border-4 border-brand-orange border-t-transparent" />
    </div>
  );
}

// ─── React Query client ───────────────────────────────────────────────────────

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000,
      retry: (failureCount, error) => {
        const status = (error as { response?: { status?: number } })?.response
          ?.status;
        if (status === 401 || status === 403 || status === 404) return false;
        return failureCount < 2;
      },
    },
  },
});

// ─── Auth guard ───────────────────────────────────────────────────────────────

function RequireAuth({
  children,
  roles,
  loginHint,
}: {
  children: React.ReactNode;
  roles?: string[];
  loginHint?: string; // e.g. 'parent'  -  appended as ?for=parent on /login redirect
}) {
  const isLoading = useIsLoading();
  const effectiveUser = useEffectiveUser();

  if (isLoading && !effectiveUser) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-brand-orange border-t-transparent" />
      </div>
    );
  }

  if (!effectiveUser) {
    const dest = loginHint ? `/login?for=${loginHint}` : "/login";
    return <Navigate to={dest} replace />;
  }

  if (roles && !roles.includes(effectiveUser.role)) {
    return <Navigate to={roleHomePath(effectiveUser.role)} replace />;
  }

  return <>{children}</>;
}

// ─── Session restore wrapper ──────────────────────────────────────────────────

function SessionRestorer({ children }: { children: React.ReactNode }) {
  useSessionRestore();
  useSocket();
  const isImpersonating = useIsImpersonating();
  return (
    <>
      <ImpersonationBanner />
      {/* Spacer pushes all layouts down by the banner's height so nothing hides under it */}
      {isImpersonating && (
        <div style={{ height: 40 }} className="flex-shrink-0" />
      )}
      {children}
    </>
  );
}

// ─── App ──────────────────────────────────────────────────────────────────────

// school_admin is the account owner: a head, or a solo teacher or tutor, so
// they get the teaching side too, with a link to the school office.
const TEACHER_ROLES = ["teacher", "teacher_trainer", "school_admin", "school_admin_teacher"];

/**
 * SmartRoot  -  renders LandingPage for visitors, AppLayout for teachers.
 * Other authenticated roles are redirected to their own home.
 */
function SmartRoot() {
  const isLoading = useIsLoading();
  const effectiveUser = useEffectiveUser();

  if (isLoading && !effectiveUser) return <PageLoader />;
  if (!effectiveUser) return <LandingPage />;
  if (!TEACHER_ROLES.includes(effectiveUser.role)) {
    return <Navigate to={roleHomePath(effectiveUser.role)} replace />;
  }
  return <AppLayout />;
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <SessionRestorer>
          <Suspense fallback={<PageLoader />}>
            <Routes>
              {/* ── Public ─────────────────────────────────────────────────── */}
              <Route path="/login" element={<LoginPage />} />
              <Route path="/verify-email" element={<VerifyEmailPage />} />
              <Route path="/signup" element={<SignupPage />} />
              <Route path="/join" element={<JoinBattlePage />} />
              <Route path="/join/:code" element={<JoinBattlePage />} />

              {/* ── SEO landing pages (public) ──────────────────────────────── */}
              {SEO_PAGES.map((p) => (
                <Route
                  key={p.slug}
                  path={`/${p.slug}`}
                  element={<SeoLandingPage slug={p.slug} />}
                />
              ))}

              {/* ── Legal pages (public) ────────────────────────────────────── */}
              {LEGAL_SLUGS.map((s) => (
                <Route
                  key={s}
                  path={`/${s}`}
                  element={<LegalPage slug={s} />}
                />
              ))}

              {/* ── Student portal ──────────────────────────────────────────── */}
              <Route path="/student/login" element={<StudentLoginPage />} />
              <Route
                path="/student/session/:sessionId"
                element={
                  <RequireStudent>
                    <StudentSessionPage />
                  </RequireStudent>
                }
              />
              {/* Alphabet pages  -  full-screen, no nav layout */}
              <Route
                path="/student/letters"
                element={
                  <RequireStudent>
                    <StudentAlphabetPage />
                  </RequireStudent>
                }
              />
              <Route
                path="/student/letters/:letter"
                element={
                  <RequireStudent>
                    <StudentLetterPage />
                  </RequireStudent>
                }
              />
              <Route
                path="/student"
                element={
                  <RequireStudent>
                    <StudentLayout />
                  </RequireStudent>
                }
              >
                <Route index element={<StudentHomePage />} />
                <Route path="games" element={<StudentGamesPage />} />
                <Route path="words" element={<StudentWordsPage />} />
                <Route path="stars" element={<StudentStarsPage />} />
                <Route path="quest" element={<StudentQuestPage />} />
                <Route path="shop" element={<StudentShopPage />} />
                <Route path="room" element={<StudentRoomPage />} />
                <Route path="homework" element={<StudentHomeworkPage />} />
                <Route
                  path="homework/:assignmentId"
                  element={<StudentTaskPage />}
                />
              </Route>

              {/* ── Platform admin ─────────────────────────────────────────── */}
              <Route
                path="/admin"
                element={
                  <RequireAuth roles={["platform_admin"]}>
                    <AdminLayout />
                  </RequireAuth>
                }
              >
                <Route index element={<AdminOverviewPage />} />
                <Route path="schools" element={<AdminSchoolsPage />} />
                <Route
                  path="schools/:schoolId"
                  element={<AdminSchoolDetail />}
                />
                <Route path="settings" element={<AdminSettingsPage />} />
                <Route path="sanctions" element={<AdminSanctionsPage />} />
                <Route path="impersonate" element={<AdminImpersonatePage />} />
              </Route>

              {/* ── Parent portal ───────────────────────────────────────────── */}
              <Route
                path="/parent"
                element={
                  <RequireAuth roles={["parent"]} loginHint="parent">
                    <ParentLayout />
                  </RequireAuth>
                }
              >
                <Route index element={<ParentDashboardPage />} />
                <Route path="messages" element={<ParentMessagesPage />} />
              </Route>

              {/* ── School admin ────────────────────────────────────────────── */}
              <Route
                path="/school"
                element={
                  <RequireAuth roles={["school_admin"]}>
                    <SchoolAdminLayout />
                  </RequireAuth>
                }
              >
                <Route index element={<SchoolDashboardPage />} />
                <Route path="teachers" element={<SchoolTeachersPage />} />
                <Route path="students" element={<SchoolStudentsPage />} />
                <Route
                  path="invite-codes"
                  element={<SchoolInviteCodesPage />}
                />
                <Route path="enrollment" element={<SchoolEnrollmentPage />} />
                <Route
                  path="billing"
                  element={<Navigate to="/school/settings" replace />}
                />
                <Route path="settings" element={<SchoolSettingsPage />} />
                <Route path="analytics" element={<SchoolAnalyticsPage />} />
                <Route path="attendance" element={<SchoolAttendancePage />} />
              </Route>

              {/* ── Live session hub  -  full-screen, no AppLayout nav ────────── */}
              <Route
                path="/classes/:classId/hub"
                element={
                  <RequireAuth roles={[...TEACHER_ROLES]}>
                    <LiveSessionHubPage />
                  </RequireAuth>
                }
              />

              {/* ── Teacher portal ──────────────────────────────────────────── */}
              {/* SmartRoot: unauthenticated → LandingPage; teacher → AppLayout */}
              <Route path="/" element={<SmartRoot />}>
                <Route index element={<Navigate to="/dashboard" replace />} />
                <Route path="dashboard" element={<DashboardPage />} />
                <Route path="classes" element={<ClassesPage />} />
                <Route path="classes/:id" element={<ClassDetailPage />} />
                <Route path="lessons" element={<LessonsPage />} />
                <Route path="planner" element={<PlannerPage />} />
                <Route path="submissions" element={<SubmissionsPage />} />
                <Route path="progress" element={<ProgressPage />} />
                <Route path="messages" element={<MessagesPage />} />
                <Route path="settings" element={<SettingsPage />} />
                <Route path="spelling" element={<SpellingPage />} />
                <Route
                  path="spelling/:listId"
                  element={<SpellingListDetailPage />}
                />
                <Route path="certificates" element={<CertificatesPage />} />
                <Route path="games" element={<GamesPage />} />
                <Route path="resources" element={<ResourcesPage />} />
                <Route path="boss-battle" element={<BossBattlePage />} />
                <Route
                  path="boss-battle/:id/project"
                  element={<BossBattleProjectorPage />}
                />
                <Route
                  path="sessions/:sessionId/log"
                  element={<SessionLogPage />}
                />
              </Route>

              {/* ── Group admin (MAT) ───────────────────────────────────────── */}
              <Route
                path="/group/overview"
                element={
                  <RequireAuth roles={["group_admin", "platform_admin"]}>
                    <GroupDashboardPage />
                  </RequireAuth>
                }
              />

              {/* ── Catch-all: redirect to role home ────────────────────────── */}
              <Route path="*" element={<RoleRedirect />} />
            </Routes>
          </Suspense>
          <CookieConsent />
          <PwaUpdatePrompt />
        </SessionRestorer>
      </BrowserRouter>
    </QueryClientProvider>
  );
}

function RoleRedirect() {
  const effectiveUser = useEffectiveUser();
  if (!effectiveUser) return <Navigate to="/login" replace />;
  return <Navigate to={roleHomePath(effectiveUser.role)} replace />;
}

function RequireStudent({ children }: { children: React.ReactNode }) {
  const { student, isLoading } = useStudentAuthStore();
  if (isLoading)
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-purple-600 border-t-transparent" />
      </div>
    );
  if (!student) {
    const returnTo = window.location.pathname + window.location.search;
    return (
      <Navigate
        to={`/student/login?returnTo=${encodeURIComponent(returnTo)}`}
        replace
      />
    );
  }
  return <>{children}</>;
}
